import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { PowerButton } from './power-theme';
import '../styles/ExportDialog.css';
import '../styles/DynamicPanel.css';

const ExportDialog = ({ isOpen, onClose, bookmarks }) => {
  const { t } = useLanguage();
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);

  if (!isOpen) return null;

  const generateNetscapeHTML = (bookmarks) => {
    const timestamp = Math.floor(Date.now() / 1000);
    
    let html = `<!DOCTYPE NETSCAPE-Bookmark-file-1>
<!-- This is an automatically generated file.
     It will be read and overwritten.
     DO NOT EDIT! -->
<META HTTP-EQUIV="Content-Type" CONTENT="text/html; charset=UTF-8">
<TITLE>Bookmarks</TITLE>
<H1>Bookmarks</H1>

<DL><p>
    <DT><H3 ADD_DATE="${timestamp}" LAST_MODIFIED="${timestamp}">BookMark Manager Export</H3>
    <DL><p>`;

    bookmarks.forEach(bookmark => {
      const addDate = bookmark.createdAt ? Math.floor(new Date(bookmark.createdAt).getTime() / 1000) : timestamp;
      const lastModified = bookmark.updatedAt ? Math.floor(new Date(bookmark.updatedAt).getTime() / 1000) : timestamp;
      
      html += `
        <DT><A HREF="${bookmark.url}" ADD_DATE="${addDate}" LAST_MODIFIED="${lastModified}"${bookmark.isFavorite ? ' ICON="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="' : ''}>${bookmark.title}</A>`;
      
      if (bookmark.description) {
        html += `
        <DD>${bookmark.description}`;
      }
    });

    html += `
    </DL><p>
</DL><p>`;

    return html;
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const htmlContent = generateNetscapeHTML(bookmarks);
      
      // Create blob and download
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
      link.href = url;
      link.download = `bookmarks_export_${dateStr}.html`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setExportComplete(true);
      
      // Auto close after 3 seconds
      setTimeout(() => {
        setExportComplete(false);
        onClose();
      }, 3000);
      
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    setExportComplete(false);
    setIsExporting(false);
    onClose();
  };

  return (
    <div className="dynamic-panel">
      {/* Power Panel Container */}
      <div className="theme-power export-power-panel">

        {/* Power Headline */}
        <div className="export-power-headline">
          <div className="export-headline-left">
            <span className="export-headline-icon">📤</span>
            <h2 className="export-headline-title">
              {t('exportBookmarks') || 'Bookmarks exportieren'}
            </h2>
          </div>
        </div>

        {!exportComplete ? (
          <>
            {/* Section 1: Export Action (Primary CTA) - ganz oben, immer sichtbar */}
            <PowerButton
              variant="primary"
              type="button"
              icon={isExporting ? null : '📤'}
              fullWidth
              onClick={handleExport}
              disabled={isExporting || bookmarks.length === 0}
            >
              {isExporting ? (
                <>
                  <span className="loading-spinner-inline"></span>
                  {t('exporting') || 'Exportiere...'}
                </>
              ) : (
                `${t('exportNow') || 'Exportieren'} (${bookmarks.length})`
              )}
            </PowerButton>

            {/* Section 2: Export Overview (Single KPI) */}
            <div className="export-overview-section">
              <div className="export-kpi-card">
                <div className="kpi-icon">🔖</div>
                <div className="kpi-content">
                  <div className="kpi-label">
                    {t('totalBookmarks') || 'Gesamte Lesezeichen'}
                  </div>
                  <div className="kpi-value">{bookmarks.length}</div>
                </div>
              </div>
            </div>

            {/* Section 3: Export Instructions (Power Info Panel) */}
            <div className="export-info-panel">
              <div className="info-panel-header">
                <h3 className="info-panel-title">
                  📋 {t('exportInstructions') || 'Export-Anweisungen'}
                </h3>
              </div>
              <ol className="export-steps">
                <li>{t('exportStep1') || 'Klicken Sie auf "Exportieren" um eine HTML-Datei zu erstellen'}</li>
                <li>{t('exportStep2') || 'Die Datei wird im Netscape-Bookmark-Format gespeichert'}</li>
                <li>{t('exportStep3') || 'Importieren Sie die Datei in Ihren Browser:'}</li>
              </ol>

              {/* Browser Support Subsection */}
              <div className="browser-support">
                <h4 className="subsection-title">
                  🌐 {t('browserSupport') || 'Browser-Unterstützung'}
                </h4>
                <ul className="browser-list">
                  <li>
                    <strong>Chrome/Edge:</strong> {t('chromeInstructions') || 'Einstellungen → Lesezeichen → Lesezeichen und Einstellungen importieren'}
                  </li>
                  <li>
                    <strong>Firefox:</strong> {t('firefoxInstructions') || 'Lesezeichen → Lesezeichen verwalten → Importieren und Sichern → Lesezeichen aus HTML importieren'}
                  </li>
                  <li>
                    <strong>Safari:</strong> {t('safariInstructions') || 'Datei → Lesezeichen importieren'}
                  </li>
                </ul>
              </div>
            </div>

          </>
        ) : (
          /* Success State */
          <div className="export-success-panel">
            <div className="success-icon">✅</div>
            <h3 className="success-title">
              {t('exportCompleted') || 'Export abgeschlossen!'}
            </h3>
            <p className="success-message">
              {t('exportSuccessMessage') || 'Ihre Bookmarks wurden erfolgreich exportiert. Die Datei wurde in Ihren Downloads gespeichert.'}
            </p>
            <p className="success-hint">
              <small>{t('autoCloseMessage') || 'Dieses Fenster schließt sich automatisch in wenigen Sekunden...'}</small>
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default ExportDialog;